import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  CircularProgress,
  Switch,
  Stack,
  Autocomplete,
  TextField,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, PieChart } from '@mui/x-charts';
import ArticleIcon from '@mui/icons-material/Article';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import axiosInstance from 'api/axiosInstance';
import { getCase } from 'api/cases';
import getCommentsByCaseId from 'api/cases/getCommentsByCaseId';
import getCaseTimeSummary, { TimeSummaryEntry } from 'api/cases/getCaseTimeSummary';
import type { CANtrackCommentRaw } from 'api/comments/getComments';

// ─── Date helpers ───────────────────────────────────────────────────────────
function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const dotNet = /\/Date\((-?\d+)\)\//.exec(dateStr);
  if (dotNet) return new Date(parseInt(dotNet[1], 10));
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(dateStr: string): string {
  const d = parseDate(dateStr);
  if (!d) return 'N/A';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(dateStr: string): string {
  const d = parseDate(dateStr);
  if (!d) return 'N/A';
  return (
    d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  );
}

function fixRelativeImagePaths(html: string): string {
  return html.replace(/src="(?!https?:\/\/|\/)(.*?)"/gi, 'src="/Portal/$1"');
}

function makeAbsoluteImagePaths(html: string): string {
  const origin = window.location.origin;
  let result = fixRelativeImagePaths(html);
  result = result.replace(/src="\/(?!\/)(.*?)"/gi, `src="${origin}/$1"`);
  return result;
}

// ─── Case search ────────────────────────────────────────────────────────────
interface CaseOption {
  Id: number;
  Title: string;
  CustomerName: string;
  ConsultantName: string;
}

function CaseSearchBar({
  onSelect,
}: {
  onSelect: (id: number) => void;
}) {
  const [options, setOptions] = useState<CaseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { notifyError } = useNotificationProvider();

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const payload = { SearchKeyword: inputValue, Page: 1, Take: 20 };
    axiosInstance
      .get(`/api/cantrackv3/ReadCases?payload=${JSON.stringify(payload)}`)
      .then(({ data }) => setOptions(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('[CaseSearchBar] Failed to search cases:', error);
        notifyError('Could not search cases. Please try again.');
        setOptions([]);
      })
      .finally(() => setLoading(false));
  }, [inputValue, notifyError]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(o) => `#${o.Id} - ${o.Title}`}
      filterOptions={(x) => x}
      loading={loading}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, val) => val && onSelect(val.Id)}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.Id}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25, py: 1 }}
        >
          <Typography variant="body2" fontWeight={700}>
            #{option.Id} &mdash; {option.Title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {option.CustomerName} &bull; {option.ConsultantName}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search cases by title..."
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
        />
      )}
      sx={{ width: '100%', maxWidth: 480 }}
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters...' : 'No cases found'}
    />
  );
}

// ─── Section wrapper ────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

// ─── Detail field ───────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: string | number | undefined }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );
}

// ─── Charts ─────────────────────────────────────────────────────────────────
function HoursByUserChart({ data, chartRef }: { data: TimeSummaryEntry[]; chartRef?: React.RefObject<HTMLDivElement> }) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(400);

  const chartData = useMemo(() => {
    const byName: Record<string, { RT: number; OT: number }> = {};
    data.forEach((e) => {
      if (!byName[e.Name]) byName[e.Name] = { RT: 0, OT: 0 };
      byName[e.Name].RT += e.RegularTime;
      byName[e.Name].OT += e.OverTime;
    });
    return Object.entries(byName).map(([name, vals]) => ({
      name,
      RT: parseFloat(vals.RT.toFixed(2)),
      OT: parseFloat(vals.OT.toFixed(2)),
    }));
  }, [data]);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        if (w > 0) setChartWidth(w);
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (chartData.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No time entries
      </Typography>
    );
  }

  return (
    <Box ref={(el: HTMLDivElement | null) => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (chartRef) (chartRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }} sx={{ width: '100%' }}>
      <BarChart
        width={chartWidth}
        height={280}
        xAxis={[
          {
            data: chartData.map((d) => d.name),
            scaleType: 'band',
            tickLabelStyle: { fontSize: '0.7rem' },
          },
        ]}
        yAxis={[{ tickLabelStyle: { fontSize: '0.75rem' } }]}
        series={[
          { data: chartData.map((d) => d.RT), label: 'Regular Time', color: theme.palette.primary.main },
          { data: chartData.map((d) => d.OT), label: 'Overtime', color: theme.palette.error.main },
        ]}
        sx={{
          '& .MuiChartsBarElement-root rect': { rx: '4px', ry: '4px' },
        }}
      />
    </Box>
  );
}

function HoursByActivityChart({ data, chartRef }: { data: TimeSummaryEntry[]; chartRef?: React.RefObject<HTMLDivElement> }) {
  const theme = useTheme();

  const chartData = useMemo(() => {
    const byActivity: Record<string, number> = {};
    data.forEach((e) => {
      const key = e.ActivityType || 'Unknown';
      byActivity[key] = (byActivity[key] || 0) + e.RegularTime + e.OverTime;
    });
    return Object.entries(byActivity)
      .map(([name, value]) => ({ id: name, value: parseFloat(value.toFixed(2)), label: name }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  if (chartData.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No time entries
      </Typography>
    );
  }

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.error.light,
    theme.palette.warning.light,
  ];

  const coloredData = chartData.map((d, i) => ({ ...d, color: colors[i % colors.length] }));

  return (
    <Box ref={chartRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <PieChart
        series={[
          {
            data: coloredData,
            innerRadius: 30,
            outerRadius: 100,
            paddingAngle: 2,
            cornerRadius: 5,
            highlightScope: { faded: 'global', highlighted: 'item' },
            faded: { innerRadius: 30, additionalRadius: -30 },
          },
        ]}
        width={420}
        height={280}
        slotProps={{
          legend: {
            direction: 'column',
            position: { vertical: 'middle', horizontal: 'right' },
          },
        }}
      />
    </Box>
  );
}

// ─── Comment card ───────────────────────────────────────────────────────────
function CommentCard({ comment }: { comment: CANtrackCommentRaw }) {
  const hasHtml = comment.Description && /<[a-z][\s\S]*>/i.test(comment.Description);

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        mb: 2,
        borderLeft: 3,
        borderLeftColor: comment.Billable === 'Y' ? 'success.main' : 'primary.main',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {comment.TaskTitle || comment.ActivityTitle || 'Untitled'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <CalendarTodayIcon sx={{ fontSize: 12 }} /> {fmtDateTime(comment.EntryDate)}
            </Typography>
            {comment.ActivityTitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <WorkIcon sx={{ fontSize: 12 }} /> {comment.ActivityTitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label={`${parseFloat(comment.TimeRegular) || 0}h${parseFloat(comment.TimeOver) > 0 ? ` + ${comment.TimeOver}h OT` : ''}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
          />
          {comment.Billable === 'Y' && (
            <Chip label="Billable" size="small" color="success" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2.5, py: 2 }}>
        {comment.Description ? (
          hasHtml ? (
            <Box
              sx={{
                '& p': { m: 0, mb: 1 },
                '& p:last-child': { mb: 0 },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                '& a': { color: 'primary.main' },
                typography: 'body2',
                lineHeight: 1.7,
              }}
              dangerouslySetInnerHTML={{ __html: fixRelativeImagePaths(comment.Description) }}
            />
          ) : (
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', m: 0, lineHeight: 1.7 }}
            >
              {comment.Description}
            </Typography>
          )
        ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No comment
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ─── Chart image capture helper ─────────────────────────────────────────────
function captureChartImage(containerEl: HTMLDivElement | null): string | null {
  if (!containerEl) return null;
  const svg = containerEl.querySelector('svg');
  if (!svg) return null;

  const svgClone = svg.cloneNode(true) as SVGSVGElement;
  const svgData = new XMLSerializer().serializeToString(svgClone);
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
}

// ─── Report HTML generator ──────────────────────────────────────────────────
interface ReportSections {
  summary: boolean;
  details: boolean;
  timeSummary: boolean;
  comments: boolean;
}

function generateReportHtml(
  caseData: any,
  caseId: number,
  sortedComments: CANtrackCommentRaw[],
  timeSummary: TimeSummaryEntry[],
  sections: ReportSections,
  barChartImage: string | null,
  pieChartImage: string | null,
  keywordList: string[],
  referenceList: string[],
): string {
  const generatedDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const escapeHtml = (str: string) =>
    str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const summaryHtml = sections.summary
    ? `
    <div class="section compact">
      <h2>Summary</h2>
      <h3>Description</h3>
      ${
        caseData.Description
          ? `<div class="description-box">${makeAbsoluteImagePaths(caseData.Description)}</div>`
          : '<p class="muted">No description</p>'
      }
      ${
        caseData.StatusComment
          ? `<h3>Status Comment</h3><div class="status-comment">${escapeHtml(caseData.StatusComment)}</div>`
          : ''
      }
    </div>`
    : '';

  const detailsHtml = sections.details
    ? `
    <div class="section compact">
      <h2>Details</h2>
      <div class="details-grid">
        <div class="details-col">
          <h3>Client Info</h3>
          <div class="field"><span class="field-label">Customer</span><span class="field-value">${escapeHtml(caseData.CustomerName || 'N/A')}</span></div>
          <div class="field"><span class="field-label">Consultant</span><span class="field-value">${escapeHtml(caseData.ConsultantName || caseData.Consultant || 'N/A')}</span></div>
          <div class="field"><span class="field-label">Primary Contact</span><span class="field-value">${escapeHtml(caseData.PrimaryContactName || caseData.PrimaryContact || 'N/A')}</span></div>
        </div>
        <div class="details-col">
          <h3>Group / Project / Deliverable</h3>
          <div class="field"><span class="field-label">Group</span><span class="field-value">${escapeHtml(caseData.GroupTitle || caseData.GroupName || 'N/A')}</span></div>
          <div class="field"><span class="field-label">Project</span><span class="field-value">${escapeHtml(caseData.ProjectTitle || caseData.ProjectName || 'N/A')}</span></div>
          <div class="field"><span class="field-label">Deliverable</span><span class="field-value">${escapeHtml(caseData.DeliverableTitle || caseData.DeliverableName || 'N/A')}</span></div>
        </div>
      </div>
      <hr />
      <div class="details-grid">
        <div class="details-col">
          <h3>Activity Info</h3>
          <div class="field"><span class="field-label">Status</span><span class="field-value">${escapeHtml(caseData.StatusTitle || caseData.Status || 'N/A')}</span></div>
          <div class="field"><span class="field-label">Created</span><span class="field-value">${fmtDate(caseData.CreatedOn)}</span></div>
          <div class="field"><span class="field-label">Last Update</span><span class="field-value">${fmtDate(caseData.UpdatedOn)}</span></div>
          <div class="field"><span class="field-label">Time Spent</span><span class="field-value">${escapeHtml(String(caseData.TimeSpent || 'N/A'))}</span></div>
          <div class="field"><span class="field-label"># of Activities</span><span class="field-value">${escapeHtml(String(caseData.NumberOfActivities || 'N/A'))}</span></div>
        </div>
        <div class="details-col">
          <h3>Keywords &amp; References</h3>
          <div class="field">
            <span class="field-label">Keywords</span>
            <span class="field-value">${keywordList.length > 0 ? keywordList.map((k) => `<span class="tag">${escapeHtml(k)}</span>`).join(' ') : 'None'}</span>
          </div>
          <div class="field">
            <span class="field-label">References</span>
            <span class="field-value">${referenceList.length > 0 ? referenceList.map((r) => `<span class="tag tag-primary">${escapeHtml(r)}</span>`).join(' ') : 'None'}</span>
          </div>
        </div>
      </div>
    </div>`
    : '';

  const timeSummaryHtml =
    sections.timeSummary && timeSummary.length > 0
      ? `
    <div class="section">
      <h2>Time Summary</h2>
      <div class="charts-grid">
        ${barChartImage ? `<div class="chart-col"><h3>Hours by Consultant</h3><img src="${barChartImage}" alt="Hours by Consultant" class="chart-img" /></div>` : ''}
        ${pieChartImage ? `<div class="chart-col"><h3>Hours by Activity Type</h3><img src="${pieChartImage}" alt="Hours by Activity Type" class="chart-img" /></div>` : ''}
      </div>
    </div>`
      : '';

  const commentsHtml = sections.comments
    ? `
    <div class="comments-section">
      <h2 class="comments-heading">Comments (${sortedComments.length})</h2>
      ${
        sortedComments.length === 0
          ? '<p class="muted">No comments for this case</p>'
          : sortedComments
              .map((c) => {
                const title = c.TaskTitle || c.ActivityTitle || 'Untitled';
                const date = fmtDateTime(c.EntryDate);
                const activity = c.ActivityTitle || '';
                const regTime = parseFloat(c.TimeRegular) || 0;
                const otTime = parseFloat(c.TimeOver) || 0;
                const timeStr = `${regTime}h${otTime > 0 ? ` + ${otTime}h OT` : ''}`;
                const billable = c.Billable === 'Y';
                const bodyHtml = c.Description
                  ? /<[a-z][\s\S]*>/i.test(c.Description)
                    ? makeAbsoluteImagePaths(c.Description)
                    : `<pre class="comment-pre">${escapeHtml(c.Description)}</pre>`
                  : '<p class="muted">No comment</p>';

                return `
              <div class="comment-card${billable ? ' billable' : ''}">
                <div class="comment-header">
                  <div>
                    <strong>${escapeHtml(title)}</strong>
                    <div class="comment-meta">${escapeHtml(date)}${activity ? ` &bull; ${escapeHtml(activity)}` : ''}</div>
                  </div>
                  <div class="comment-badges">
                    <span class="badge">${timeStr}</span>
                    ${billable ? '<span class="badge badge-billable">Billable</span>' : ''}
                  </div>
                </div>
                <div class="comment-body">${bodyHtml}</div>
              </div>`;
              })
              .join('\n')
      }
    </div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Case #${caseId} - ${escapeHtml(caseData.Title || 'Report')}</title>
  <style>
    /* ── Print layout ─────────────────────────────────────────── */
    @page {
      size: landscape;
      margin: 1.5cm;
    }
    @media print {
      .no-print { display: none !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0;
      }
      /* Let content flow naturally across pages */
      .section { page-break-inside: auto; }
      /* Keep short blocks together when possible */
      .section.compact { page-break-inside: avoid; }
      .comment-card { page-break-inside: auto; }
      /* Never orphan a comment header from its body */
      .comment-header { page-break-after: avoid; }
      .comment-body { page-break-before: avoid; }
      /* Charts stay together */
      .charts-grid { page-break-inside: avoid; }
      /* Images must not overflow the page */
      img { max-width: 100% !important; max-height: 90vh !important; object-fit: contain; }
    }

    /* ── Base ──────────────────────────────────────────────────── */
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    html { font-size: 14px; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #1a1a1a; line-height: 1.6; padding: 24px; background: #fff;
      max-width: 1200px; margin: 0 auto;
    }

    /* ── Top button bar ───────────────────────────────────────── */
    .no-print { text-align: center; padding: 12px 0 24px; }
    .no-print button {
      padding: 10px 28px; font-size: 14px; font-weight: 600; cursor: pointer;
      background: #1976d2; color: #fff; border: none; border-radius: 6px;
    }
    .no-print button:hover { background: #1565c0; }

    /* ── Report header ────────────────────────────────────────── */
    .report-header { margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e0e0e0; }
    .report-header h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
    .report-header .meta { font-size: 13px; color: #666; }
    .report-header .status-chip {
      display: inline-block; padding: 2px 10px; border-radius: 12px;
      background: #e3f2fd; color: #1565c0; font-size: 12px; font-weight: 600; margin-right: 8px;
    }

    /* ── Sections ─────────────────────────────────────────────── */
    .section {
      margin-bottom: 24px; padding: 20px;
      border: 1px solid #e0e0e0; border-radius: 8px;
    }
    .section h2 { font-size: 16px; font-weight: 700; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 1px solid #eee; }
    .section h3 { font-size: 13px; font-weight: 700; color: #666; margin-bottom: 8px; margin-top: 4px; }

    /* ── Description / Status comment ─────────────────────────── */
    .description-box {
      padding: 12px; border: 1px solid #e0e0e0; border-radius: 6px;
      font-size: 14px; line-height: 1.7; overflow-wrap: break-word; word-break: break-word;
    }
    .description-box p { margin: 0 0 8px; }
    .description-box p:last-child { margin-bottom: 0; }
    .description-box img { max-width: 100%; height: auto; border-radius: 4px; }
    .status-comment {
      padding: 12px; background: #fff8e1; border: 1px solid #ffcc02;
      border-radius: 6px; font-size: 14px;
    }

    /* ── Details two-column ───────────────────────────────────── */
    .details-grid { display: table; width: 100%; table-layout: fixed; }
    .details-col { display: table-cell; width: 50%; vertical-align: top; padding-right: 16px; }
    .details-col:last-child { padding-right: 0; padding-left: 16px; }
    .field { margin-bottom: 10px; }
    .field-label { display: block; font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.3px; }
    .field-value { font-size: 14px; font-weight: 500; overflow-wrap: break-word; }
    hr { border: none; border-top: 1px solid #eee; margin: 16px 0; }
    .tag { display: inline-block; padding: 1px 8px; border: 1px solid #ccc; border-radius: 10px; font-size: 12px; margin: 2px 2px; }
    .tag-primary { border-color: #1976d2; color: #1976d2; }

    /* ── Charts ────────────────────────────────────────────────── */
    .charts-grid { display: table; width: 100%; table-layout: fixed; }
    .chart-col { display: table-cell; width: 50%; vertical-align: top; text-align: center; padding: 0 8px; }
    .chart-img { max-width: 100%; height: auto; }

    /* ── Comments ──────────────────────────────────────────────── */
    .comments-section { margin-bottom: 24px; }
    .comments-heading {
      font-size: 16px; font-weight: 700; margin-bottom: 14px;
      padding: 20px 20px 12px; border: 1px solid #e0e0e0; border-bottom: none;
      border-radius: 8px 8px 0 0; border-bottom: 1px solid #eee;
    }
    .comment-card {
      border: 1px solid #e0e0e0; border-radius: 8px;
      margin-bottom: 14px; border-left: 3px solid #1976d2;
      overflow: visible;
    }
    .comment-card.billable { border-left-color: #2e7d32; }
    .comment-header {
      padding: 10px 16px; background: #fafafa; border-bottom: 1px solid #eee;
      display: flex; justify-content: space-between; align-items: flex-start;
      flex-wrap: wrap; gap: 8px;
    }
    .comment-header strong { font-size: 14px; }
    .comment-meta { font-size: 12px; color: #888; margin-top: 2px; }
    .comment-badges { display: flex; gap: 6px; align-items: center; }
    .badge { display: inline-block; padding: 2px 8px; border: 1px solid #ccc; border-radius: 10px; font-size: 11px; font-weight: 600; }
    .badge-billable { background: #e8f5e9; border-color: #2e7d32; color: #2e7d32; }
    .comment-body {
      padding: 12px 16px; font-size: 14px; line-height: 1.7;
      overflow-wrap: break-word; word-break: break-word;
    }
    .comment-body img { max-width: 100%; height: auto; border-radius: 4px; }
    .comment-body p { margin: 0 0 8px; }
    .comment-body p:last-child { margin-bottom: 0; }
    .comment-pre { white-space: pre-wrap; word-break: break-word; font-family: inherit; margin: 0; }

    .muted { color: #999; font-style: italic; font-size: 14px; }
  </style>
</head>
<body>
  <div class="no-print">
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>

  <div class="report-header">
    <h1>Case ${caseId}: ${escapeHtml(caseData.Title || '')}</h1>
    <div class="meta">
      <span class="status-chip">${escapeHtml(caseData.StatusTitle || caseData.Status || '')}</span>
      Generated ${generatedDate}
    </div>
  </div>

  ${summaryHtml}
  ${detailsHtml}
  ${timeSummaryHtml}
  ${commentsHtml}
</body>
</html>`;
}

// ─── Main page ──────────────────────────────────────────────────────────────
function CasePrintPage() {
  const { caseId: caseIdParam } = useParams<{ caseId: string }>();
  const [caseId, setCaseId] = useState<number | null>(caseIdParam ? Number(caseIdParam) : null);
  const [caseData, setCaseData] = useState<any>(null);
  const [comments, setComments] = useState<CANtrackCommentRaw[]>([]);
  const [timeSummary, setTimeSummary] = useState<TimeSummaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ascending, setAscending] = useState(false);
  const { notifyError } = useNotificationProvider();

  const [sections, setSections] = useState<ReportSections>({
    summary: true,
    details: true,
    timeSummary: true,
    comments: true,
  });

  const barChartRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<HTMLDivElement>(null);

  const toggleSection = (key: keyof ReportSections) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const loadCase = useCallback(
    async (id: number) => {
      setLoading(true);
      setError(null);
      setCaseId(id);
      try {
        const [caseRes, commentsRes, timeRes] = await Promise.all([
          getCase(id),
          getCommentsByCaseId(id),
          getCaseTimeSummary(id).catch((err) => {
            console.warn('[CasePrintPage] Time summary unavailable:', err);
            return [];
          }),
        ]);
        setCaseData(caseRes);
        setComments(commentsRes);
        setTimeSummary(timeRes);
        document.title = `Case #${id} - ${caseRes?.Title || 'Report'}`;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load case report';
        setError(message);
        console.error('[CasePrintPage] Failed to load case:', { caseId: id, error: err });
        notifyError('Could not load case report. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [notifyError],
  );

  useEffect(() => {
    if (caseId && caseId > 0) loadCase(caseId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedComments = useMemo(() => {
    const sorted = [...comments].sort((a, b) => {
      const da = parseDate(a.EntryDate)?.getTime() || 0;
      const db = parseDate(b.EntryDate)?.getTime() || 0;
      return db - da;
    });
    return ascending ? sorted.reverse() : sorted;
  }, [comments, ascending]);

  const keywordList =
    caseData?.Keywords?.map?.((k: any) => k.Title) ??
    (typeof caseData?.Keywords === 'string' && caseData.Keywords
      ? caseData.Keywords.split(';;;').filter(Boolean)
      : []);

  const referenceList =
    caseData?.References?.map?.((r: any) => r.Title) ??
    (typeof caseData?.References === 'string' && caseData.References
      ? caseData.References.split(';;;').filter(Boolean)
      : []);

  const handleGenerateReport = () => {
    if (!caseData || !caseId) return;

    const barImg = sections.timeSummary ? captureChartImage(barChartRef.current) : null;
    const pieImg = sections.timeSummary ? captureChartImage(pieChartRef.current) : null;

    const html = generateReportHtml(
      caseData,
      caseId,
      sortedComments,
      timeSummary,
      sections,
      barImg,
      pieImg,
      keywordList,
      referenceList,
    );

    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      notifyError('Could not open report window. Please allow pop-ups for this site.');
      return;
    }
    reportWindow.document.write(html);
    reportWindow.document.close();
  };

  const selectedCount = Object.values(sections).filter(Boolean).length;

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* ── Control bar ──────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
            Case Report Builder
          </Typography>

          <CaseSearchBar onSelect={loadCase} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="caption"
                color={ascending ? 'text.disabled' : 'error.main'}
                fontWeight={700}
                sx={{ fontSize: '0.65rem' }}
              >
                DESC
              </Typography>
              <Switch
                size="small"
                checked={ascending}
                onChange={(_, v) => setAscending(v)}
              />
              <Typography
                variant="caption"
                color={ascending ? 'success.main' : 'text.disabled'}
                fontWeight={700}
                sx={{ fontSize: '0.65rem' }}
              >
                ASC
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* ── Section toggles + Generate button ────────────────────── */}
      {caseData && !loading && !error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
              Include:
            </Typography>
            <FormControlLabel
              control={<Checkbox size="small" checked={sections.summary} onChange={() => toggleSection('summary')} />}
              label={<Typography variant="body2">Summary</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={sections.details} onChange={() => toggleSection('details')} />}
              label={<Typography variant="body2">Details</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={sections.timeSummary} onChange={() => toggleSection('timeSummary')} />}
              label={<Typography variant="body2">Time Summary Charts</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={sections.comments} onChange={() => toggleSection('comments')} />}
              label={<Typography variant="body2">Comments</Typography>}
            />
            <Box sx={{ ml: 'auto' }}>
              <Tooltip title={selectedCount === 0 ? 'Select at least one section to include' : 'Open report in a new window for printing'}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<ArticleIcon />}
                    onClick={handleGenerateReport}
                    disabled={selectedCount === 0}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Generate Report
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── Content (preview) ────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => caseId && loadCase(caseId)} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Paper>
      )}

      {!loading && !error && !caseData && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
          }}
        >
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No case selected
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Search for a case above to generate a report
          </Typography>
        </Paper>
      )}

      {!loading && !error && caseData && (
        <>
          {/* Title */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              Case {caseId}: {caseData.Title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={caseData.StatusTitle || caseData.Status}
                size="small"
                color="primary"
              />
              <Typography variant="caption" color="text.secondary">
                Generated{' '}
                {new Date().toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
          </Paper>

          {/* Summary */}
          {sections.summary && (
            <Section title="Summary">
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                Description
              </Typography>
              {caseData.Description ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    '& p': { m: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& img': { maxWidth: '100%', height: 'auto' },
                    typography: 'body2',
                    lineHeight: 1.7,
                  }}
                  dangerouslySetInnerHTML={{ __html: fixRelativeImagePaths(caseData.Description) }}
                />
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                  No description
                </Typography>
              )}

              {caseData.StatusComment && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Status Comment
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'warning.light',
                      borderColor: 'warning.main',
                      typography: 'body2',
                    }}
                  >
                    {caseData.StatusComment}
                  </Paper>
                </>
              )}
            </Section>
          )}

          {/* Details */}
          {sections.details && (
            <Section title="Details">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                    Client Info
                  </Typography>
                  <Field label="Customer" value={caseData.CustomerName} />
                  <Field label="Consultant" value={caseData.ConsultantName || caseData.Consultant} />
                  <Field label="Primary Contact" value={caseData.PrimaryContactName || caseData.PrimaryContact} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                    Group / Project / Deliverable
                  </Typography>
                  <Field label="Group" value={caseData.GroupTitle || caseData.GroupName} />
                  <Field label="Project" value={caseData.ProjectTitle || caseData.ProjectName} />
                  <Field label="Deliverable" value={caseData.DeliverableTitle || caseData.DeliverableName} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                    Activity Info
                  </Typography>
                  <Field label="Status" value={caseData.StatusTitle || caseData.Status} />
                  <Field label="Created" value={fmtDate(caseData.CreatedOn)} />
                  <Field label="Last Update" value={fmtDate(caseData.UpdatedOn)} />
                  <Field label="Time Spent" value={caseData.TimeSpent} />
                  <Field label="# of Activities" value={caseData.NumberOfActivities} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                    Keywords & References
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Keywords
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {keywordList.length > 0 ? (
                        keywordList.map((kw: string, i: number) => (
                          <Chip key={i} label={kw} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          None
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      References
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {referenceList.length > 0 ? (
                        referenceList.map((ref: string, i: number) => (
                          <Chip key={i} label={ref} size="small" variant="outlined" color="primary" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          None
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Section>
          )}

          {/* Time Summary Charts */}
          {sections.timeSummary && (
            <Section title="Time Summary">
              {timeSummary.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  No time entries for this case
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                      Hours by Consultant
                    </Typography>
                    <HoursByUserChart data={timeSummary} chartRef={barChartRef} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                      Hours by Activity Type
                    </Typography>
                    <HoursByActivityChart data={timeSummary} chartRef={pieChartRef} />
                  </Grid>
                </Grid>
              )}
            </Section>
          )}

          {/* Comments */}
          {sections.comments && (
            <Box>
              <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Comments ({comments.length})
                </Typography>
                {sortedComments.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}
                  >
                    <Typography variant="body2" color="text.disabled">
                      No comments for this case
                    </Typography>
                  </Paper>
                ) : (
                  sortedComments.map((c) => <CommentCard key={c.Id} comment={c} />)
                )}
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default CasePrintPage;
